import React, { useEffect, useRef, useState } from 'react';
import Validator from 'validatorjs';
import { useIMask } from 'react-imask';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import FaAsyncTypeahead from './fa-typahead/FaAsyncTypeahead';
import CalendarDatePicker from './calendar-date-picker/CalendarDatePicker';
import CalendarDateTimePicker from './calendar-datetime-picker/CalendarDateTimePicker';

const FormControl = (props) => {
  const [validator, setValidator] = useState(
    !!props.rules ? new Validator({}, {[props.name]: props.rules || []}) : null
  );

  const [showPassword, setshowPassword] = useState(false);

  const { error, required, ...more } = props;

  const {
    ref,
  } = useIMask({ mask: props.mask }, { onAccept: (value, mask) => {
    const event = {
      target: {
        name: props.name,
        value: value
      }
    }

    handleOnChange(event);
  }});

  useEffect(() => {
    const { model = {} } = props;

    const newData = {
      ...model,
      [name]: props.value
    };

    !!rules && validate(newData);
  }, [props.rules, props.value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!!mask && !!ref && !!ref.current) {
      ref.current.value = value !== null && value !== undefined ? value : '';
    }
  }, [props.value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const { name, value } = props;

    if (value !== null && value !== undefined) {
      const { rules, model = {} } = props;

      const newData = {
        ...model,
        [name]: value
      };

      !!rules && validate(newData);
    }
  }, [props.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const isRequired = () => {
    if (required) {
      return true
    }

    const { name, rules: propRules } = props;
    const validator = getValidator();

    if (!!validator) {
      const rules = propRules || validator.rules[name] || [];

      for (const rule of rules) {
        let validation = typeof rule === 'string' ? rule : rule.name;

        if (validation === 'required') {
          return true;
        }
      }
    }

    return false;
  }

  const isInvalid = () => {
    const { name } = props;

    if (error) {
      return true;
    }

    const validator = getValidator();

    return !!validator
        && props.value !== undefined
        && validator.errors.has(name)
  }

  const getValidator = () => {
    return validator || props.validator;
  }

  const validate = (data) => {
    const { name, rules } = props;
    const validator = new Validator(data, {[name]: rules});

    validator.check();

    setValidator(validator);

    return validator;
  }

  const handleOnChange = (event) => {
    const { name, value: tmpValue } = event.target;
    const { model = {}, onChange, casing = null } = props;

    let value = tmpValue;

    switch (casing) {
      case 'upper':
        value = value.toUpperCase();
        break;

      default:
        break;
    }

    const newData = {
      ...model,
      [name]: value
    };

    !!onChange && onChange(newData, event);
  }

  const handleOnCheckChange = (event) => {
    const { checked } = event.target;

    const { model = {}, onChange} = props;

    const newData = {
      ...model,
      [name]: checked
    };

    !!onChange && onChange(newData, event);
  }

  const handleOnSelect = (value) => {
    const { name, model = {}, onChange } = props;

    !!onChange && onChange({
      ...model,
      [name]: !!value ? (value.id || value.value) : ''
    });
  }

  const renderErrors = () => {
    const { name } = props;
    const validator = getValidator();

    if (props.value === undefined) {
      return null;
    }

    return (
      <Form.Control.Feedback type="invalid">
        { validator.errors.get(name).map((value, index) => {
            return <label key={ index }>{ value }</label>
          })
        }
      </Form.Control.Feedback>
    );
  }

  const {
    validator: parentValidator,
    id,
    label,
    name,
    mask,
    value,
    placeholder = '',
    help,
    type = 'text',
    options = [],
    children,
    rules,
    inline = false,
    className: classNameOriginal,
    ...rest
  } = more;

  let controlProps = {
    ...rest,
    id: id || name,
    name,
    value: value !== null && value !== undefined ? value : '',
    placeholder
  }

  if (['select'].indexOf(type) !== -1) {
    controlProps.as = type
  } else {
    controlProps.type = type;
  }

  let inputRef = useRef(null);

  const onRef = (element) => {
      inputRef.current = element;

      if (props.mask) {
        ref.current = element;
      }

      !!props.onRef && props.onRef(inputRef);
  }

  const classNames = Array.isArray(classNameOriginal) ? classNameOriginal : [classNameOriginal];

  const className = isRequired() && controlProps.value === undefined ? 'text-danger' : '';
  const highlight = isRequired() && (props.value === undefined || props.value === '');

  if (highlight || (isRequired() && isInvalid())) {
    classNames.push('form-control-required');
  } else if (isRequired() && !isInvalid()) {
    if (!!rules) {
      classNames.push('form-control-filled');
    } else if (error === undefined) {
      classNames.push('form-control-required');
    } else if (error === false) {
      classNames.push('form-control-filled');
    }
  }

  let selectedOption = [];

  if (type === 'ahead' && !!value && !!options && options.length > 0) {
    selectedOption = [options.find(item => (item.id || item.value) === value)]
  }

  if (!!mask) {
    delete controlProps.value;
  }

  if (type === 'checkbox') {
    const { checked } = props;

    if (checked !== undefined) {
      controlProps.checked = checked;
    } else {
      controlProps.checked = value || false;
    }

    controlProps.label = label;
  }

  return (
    <div className={ !!inline ? 'form-inline' : '' }>
      <Form.Group>
        { !!label && !['checkbox'].includes(type) && (
          <Form.Label className={ highlight ? '' : '' } htmlFor={id || name}>
            { label }
            <strong className={ `required-indicator ${className}` }>{ isRequired() ? ' * ' : ''}</strong>
          </Form.Label>
          )
        }

        { type === 'ahead' && (
          <FaAsyncTypeahead
            help=""
            onSelect={ handleOnSelect }
            options={ options }
            selected={ selectedOption || [] }
            isInvalid={ isRequired() && controlProps.value === undefined }
            onSetRef={ onRef }
            { ...rest }
            id={ id || name }
          />
        )}

        { type === 'date-picker' && (
          <CalendarDatePicker
            { ...controlProps }
            id={ id || name }
          />
        )}

        { type === 'datetime-picker' && (
          <CalendarDateTimePicker
            { ...controlProps }
            id={ id || name }
          />
        )}

        { !['ahead', 'date-picker', 'datetime-picker', 'password-preview', 'checkbox'].includes(type) && (
          <Form.Control
            { ...controlProps }
            className={ classNames }
            isInvalid={ isInvalid() }
            onChange={ handleOnChange }
            ref={ onRef }
          >
            {
              type === 'select' && !!options
                ? options.map((item, index) => <option key={ index } value={ item.value }>{ item.label }</option>)
                : null
            }
          </Form.Control>
        )}

        { ['checkbox'].includes(type) && (
          <Form.Check
            { ...controlProps }
            className={ classNames }
            isInvalid={ isInvalid() }
            checked={value || false}
            onChange={ handleOnCheckChange }
            ref={ onRef }
          />
        )}

        { type === 'password-preview' && (
          <InputGroup>
             <Form.Control
              { ...controlProps }
              type={ showPassword ? "text" : "password" }
              isInvalid={ isInvalid() }
              onChange={ handleOnChange }
              ref={ onRef }
            >
            </Form.Control>
            <Button variant="secondary"
              onClick={ () => setshowPassword(!showPassword) }
            >
              { !showPassword ? <FontAwesomeIcon icon={ ['fas', 'eye'] } /> : <FontAwesomeIcon icon={ ['fas', 'eye-slash'] } /> }
            </Button>
          </InputGroup>
        )}

        { !!help && (
          <Form.Text className="text-muted">
            { help }
          </Form.Text>
        )}

        { !error && isInvalid() && renderErrors() }

        {!!error && (
          <Form.Control.Feedback type="invalid">
            <label>{error}</label>
          </Form.Control.Feedback>
        )}
      </Form.Group>

      { children }

    </div>
  )
}

export default FormControl;
